import { utils } from 'apprise-core/utils'
import { useL, useMultilang } from 'apprise-frontend-core/intl/multilang'

import { TagCategory, unknownCategory } from 'apprise-core/category'
import { CustomProperties, ExpressionTerm, Tag, TagExpression, TagReference, unknownTag } from 'apprise-core/tag'
import { OneOrMore, Optional } from 'apprise-core/utils'
import { useTagExcludes } from 'apprise-frontend-tags/tag/context'


export const useTagModel = () => {

    const l = useL()
    const ml = useMultilang()

    const { isExcluded } = useTagExcludes()

    const self = {


        // returns the text fingerprint of one or more tags.
        textOf: (tags: OneOrMore<Optional<Tag>>) => utils().purge(utils().arrayOf(tags)).flatMap(t => [l(t.name), t.code])

        ,

        newTag: (): Tag => ({

            id: undefined!,
            category: undefined!,
            // if it cannot be activated starts already active. 
            lifecycle: { state: isExcluded('active') ? 'active' : 'inactive' },
            name: {},
            description: {},
            properties: {}

        })

        ,

        newTagInCategory: (category: TagCategory): Tag => ({

            id: undefined!,
            category: category.id,
            lifecycle: { state: 'inactive' },
            name: {},
            description: {},
            properties: {}
        })

        ,

        // returns a tag where none can't be found: we have no id or the id cannot be resolved.
        noTag: (name?: Optional<TagReference>): Tag => ({

            id: unknownTag,
            category: unknownCategory,
            lifecycle: { state: 'active' },
            name: { en: name || undefined },
            description: undefined!,
            properties: {}
        })

        ,

        comparator: (o1: Tag, o2: Tag) => {

            if (o1.properties.sort || o2.properties.sort) {
                const o1SortValue = o1.properties.sort ?? Number.MAX_VALUE
                const o2SortValue = o2.properties.sort ?? Number.MAX_VALUE

                if (o1SortValue === o2SortValue) return ml.comparator(o1.name, o2.name)

                return o1SortValue - o2SortValue

            }

            return ml.comparator(o1.name, o2.name)

        }

        ,

        // compares tags but puts one particular tag before all others (used for context)
        biasedComparator: (bias: Tag) => (o1: Tag, o2: Tag) => l(bias.name) === l(o1.name) ? l(bias.name) === l(o2.name) ? 0 : -1 : l(bias.name) === l(o2.name) ? 1 : self.comparator(o1, o2)

        ,

        // compares tags but puts one particular tag before all others (used for context)
        codeComparator: utils().compareStringsOf((o: Tag) => o.code)

        ,



        given: (tags: Tag[]) => {

            const api = {


                allWithCategory: (catRef: string) => tags.filter(t => t.category === catRef),
                matches: (e: TagExpression | undefined) => !e || self.expression(e).matches(tags),
                filterMatching: (othertags: Tag[] = tags) => othertags.filter(t => !t.properties.includeCondition || api.matches(t.properties.includeCondition))

            }


            return api
        }

        ,

        expression: (expr: TagExpression) => ({

            matches: (tags: Tag[]) => {

                const evaluateTerm = (term: ExpressionTerm) => {

                    const targets = self.given(tags).allWithCategory(term.category).map(t => t.id)

                    if ((term.tags?.length ?? 0) === 0)
                        return true

                    const matched = targets.filter(t => term.tags?.includes(t))

                    let match: boolean;

                    switch (term.op) {

                        case 'anyOf': match = matched.length > 0; break
                        case 'allOf': match = matched.length === term.tags.length; break
                        default: match = matched.length === 0; break

                    }

                    //console.log("matching",t,"with term",term,"results in",match)

                    return match
                }


                return !expr.terms.map(evaluateTerm).some(r => r === false)

            }
        })

        ,

        clone: (tag: Tag): Tag => ({ ...utils().deepclone(tag), id: undefined!, lifecycle: { state: 'inactive' } })

        ,

        customProperties: <T extends CustomProperties>(tag: Tag) => tag.properties.custom as T

    }


    return self
}